<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { X, Check } from '@lucide/vue'
import type { ButtonSize } from '../../types'

export type ColorPickerPreset = {
  label?: string
  colors: string[]
  defaultOpen?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null
    value?: string | number | null
    defaultValue?: string
    disabled?: boolean
    disabledAlpha?: boolean
    allowClear?: boolean
    showText?: boolean | ((color: string) => string)
    size?: ButtonSize
    presets?: ColorPickerPreset[] | string[]
    placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight'
    trigger?: 'click' | 'hover'
    open?: boolean
    title?: string
    placeholder?: string
    block?: boolean
  }>(),
  {
    modelValue: undefined,
    value: undefined,
    defaultValue: '#5A46E0',
    disabled: false,
    disabledAlpha: true,
    allowClear: false,
    showText: true,
    size: 'middle',
    presets: undefined,
    placement: 'bottomLeft',
    trigger: 'click',
    open: undefined,
    title: undefined,
    placeholder: '请选择颜色',
    block: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [color: string]
  'update:value': [color: string]
  'update:open': [open: boolean]
  change: [color: string]
  changeComplete: [color: string]
  openChange: [open: boolean]
  clear: []
}>()

// --- Color Math Helpers ---
function parseColorString(str?: string | number | null): { r: number; g: number; b: number; a: number } | null {
  if (str == null) return null
  const s = String(str).trim()
  if (!s) return null

  if (s.startsWith('#')) {
    const hex = s.slice(1)
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: 1
      }
    }
    if (hex.length === 4) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: Math.round((parseInt(hex[3] + hex[3], 16) / 255) * 100) / 100
      }
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1
      }
    }
    if (hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: Math.round((parseInt(hex.slice(6, 8), 16) / 255) * 100) / 100
      }
    }
  }

  const rgbMatch = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i)
  if (rgbMatch) {
    return {
      r: Math.min(255, Math.max(0, parseInt(rgbMatch[1], 10))),
      g: Math.min(255, Math.max(0, parseInt(rgbMatch[2], 10))),
      b: Math.min(255, Math.max(0, parseInt(rgbMatch[3], 10))),
      a: rgbMatch[4] !== undefined ? Math.min(1, Math.max(0, parseFloat(rgbMatch[4]))) : 1
    }
  }

  return null
}

function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  h = ((h % 360) + 360) % 360
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let r = 0, g = 0, b = 0
  if (h < 60) {
    r = c; g = x; b = 0
  } else if (h < 120) {
    r = x; g = c; b = 0
  } else if (h < 180) {
    r = 0; g = c; b = x
  } else if (h < 240) {
    r = 0; g = x; b = c
  } else if (h < 300) {
    r = x; g = 0; b = c
  } else {
    r = c; g = 0; b = x
  }
  return [
    Math.min(255, Math.max(0, Math.round((r + m) * 255))),
    Math.min(255, Math.max(0, Math.round((g + m) * 255))),
    Math.min(255, Math.max(0, Math.round((b + m) * 255)))
  ]
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  const s = max === 0 ? 0 : d / max
  const v = max

  if (d !== 0) {
    if (max === r) {
      h = ((g - b) / d + (g < b ? 6 : 0)) * 60
    } else if (max === g) {
      h = ((b - r) / d + 2) * 60
    } else {
      h = ((r - g) / d + 4) * 60
    }
  }
  return [Math.round(h), s, v]
}

function rgbToHex(r: number, g: number, b: number, a = 1, includeAlpha = false): string {
  const hexR = r.toString(16).padStart(2, '0').toUpperCase()
  const hexG = g.toString(16).padStart(2, '0').toUpperCase()
  const hexB = b.toString(16).padStart(2, '0').toUpperCase()
  if (includeAlpha && a < 1) {
    const hexA = Math.round(a * 255).toString(16).padStart(2, '0').toUpperCase()
    return `#${hexR}${hexG}${hexB}${hexA}`
  }
  return `#${hexR}${hexG}${hexB}`
}

// --- State ---
const rawInput = computed(() => {
  if (props.modelValue !== undefined) return props.modelValue
  if (props.value !== undefined) return props.value
  return props.defaultValue
})

const hue = ref(248)
const saturation = ref(0.69)
const value = ref(0.88)
const alpha = ref(1)
const hexInputText = ref('')

function syncFromInput(colorStr?: string | number | null) {
  const parsed = parseColorString(colorStr)
  if (parsed) {
    const [h, s, v] = rgbToHsv(parsed.r, parsed.g, parsed.b)
    if (s > 0 || v > 0) {
      hue.value = h
    }
    saturation.value = s
    value.value = v
    alpha.value = parsed.a
    hexInputText.value = rgbToHex(parsed.r, parsed.g, parsed.b, parsed.a, !props.disabledAlpha)
  } else {
    hexInputText.value = colorStr ? String(colorStr) : ''
  }
}

watch(rawInput, (val) => {
  syncFromInput(val)
}, { immediate: true })

const currentRgb = computed(() => hsvToRgb(hue.value, saturation.value, value.value))
const currentHex = computed(() => {
  const parsed = parseColorString(rawInput.value)
  if (!parsed && !rawInput.value) return ''
  const [r, g, b] = currentRgb.value
  return rgbToHex(r, g, b, alpha.value, !props.disabledAlpha)
})

const hueColorRgb = computed(() => hsvToRgb(hue.value, 1, 1))

// --- Presets ---
const defaultPresetColors = [
  '#5A46E0',
  '#2F6FED',
  '#157A55',
  '#946008',
  '#BE3038',
  '#171A1F',
  '#475569',
  '#64748B',
  '#94A3B8',
  '#CBD5E1',
  '#F1F5F9',
  '#FFFFFF'
]

const normalizedPresets = computed<ColorPickerPreset[]>(() => {
  if (!props.presets) {
    return [{ label: '推荐预设', colors: defaultPresetColors }]
  }
  if (Array.isArray(props.presets) && props.presets.length > 0 && typeof props.presets[0] === 'string') {
    return [{ label: '预设颜色', colors: props.presets as string[] }]
  }
  return props.presets as ColorPickerPreset[]
})

// --- Popover Visibility ---
const PANEL_WIDTH = 248
const PANEL_GAP = 4
const VIEWPORT_MARGIN = 8

const containerRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const panelStyle = ref<Record<string, string>>({})
const isControlledOpen = computed(() => props.open !== undefined)
const innerOpen = ref(false)
const isOpen = computed(() => (isControlledOpen.value ? Boolean(props.open) : innerOpen.value))

function setOpen(val: boolean) {
  if (props.disabled) return
  if (val) updatePanelPosition()
  if (!isControlledOpen.value) {
    innerOpen.value = val
  }
  emit('update:open', val)
  emit('openChange', val)
}

function toggleOpen() {
  if (props.disabled) return
  setOpen(!isOpen.value)
}

function updatePanelPosition() {
  if (typeof window === 'undefined' || !containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  const panelHeight = panelRef.value?.offsetHeight ?? 280
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
  const placeAbove = spaceBelow < panelHeight && rect.top > spaceBelow
  let top = placeAbove ? rect.top - PANEL_GAP - panelHeight : rect.bottom + PANEL_GAP
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, window.innerHeight - panelHeight - VIEWPORT_MARGIN))
  const alignRight = props.placement === 'bottomRight' || props.placement === 'topRight'
  let left = alignRight ? rect.right - PANEL_WIDTH : rect.left
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN))
  panelStyle.value = {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`
  }
}

function handleDocumentPointerDown(e: Event) {
  const target = e.target as Node
  if (containerRef.value?.contains(target) || panelRef.value?.contains(target)) return
  setOpen(false)
}

function bindPanelListeners() {
  if (typeof document === 'undefined') return
  document.addEventListener('pointerdown', handleDocumentPointerDown, true)
  window.addEventListener('resize', updatePanelPosition)
  window.addEventListener('scroll', updatePanelPosition, true)
}

function unbindPanelListeners() {
  if (typeof document === 'undefined') return
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  window.removeEventListener('resize', updatePanelPosition)
  window.removeEventListener('scroll', updatePanelPosition, true)
}

watch(isOpen, async (open) => {
  if (!open) {
    unbindPanelListeners()
    return
  }
  await nextTick()
  updatePanelPosition()
  await nextTick()
  updatePanelPosition()
  bindPanelListeners()
})

onBeforeUnmount(() => {
  unbindPanelListeners()
})

// --- Value Emit Helper ---
function emitColor(newHex: string, isComplete = false) {
  emit('update:modelValue', newHex)
  emit('update:value', newHex)
  emit('change', newHex)
  if (isComplete) {
    emit('changeComplete', newHex)
  }
}

// --- Saturation/Value Dragging ---
const paletteRef = ref<HTMLElement | null>(null)
let isDraggingPalette = false

function updatePaletteFromEvent(e: MouseEvent, isEnd = false) {
  if (!paletteRef.value) return
  const rect = paletteRef.value.getBoundingClientRect()
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
  const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))
  saturation.value = x / rect.width
  value.value = 1 - y / rect.height
  const [r, g, b] = hsvToRgb(hue.value, saturation.value, value.value)
  const hex = rgbToHex(r, g, b, alpha.value, !props.disabledAlpha)
  hexInputText.value = hex
  emitColor(hex, isEnd)
}

function handlePaletteMouseDown(e: MouseEvent) {
  if (props.disabled) return
  isDraggingPalette = true
  updatePaletteFromEvent(e, false)

  function onMouseMove(event: MouseEvent) {
    if (isDraggingPalette) {
      updatePaletteFromEvent(event, false)
    }
  }
  function onMouseUp(event: MouseEvent) {
    if (isDraggingPalette) {
      isDraggingPalette = false
      updatePaletteFromEvent(event, true)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

// --- Hue Slider Dragging ---
const hueTrackRef = ref<HTMLElement | null>(null)
let isDraggingHue = false

function updateHueFromEvent(e: MouseEvent, isEnd = false) {
  if (!hueTrackRef.value) return
  const rect = hueTrackRef.value.getBoundingClientRect()
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
  hue.value = Math.round((x / rect.width) * 360) % 360
  const [r, g, b] = hsvToRgb(hue.value, saturation.value, value.value)
  const hex = rgbToHex(r, g, b, alpha.value, !props.disabledAlpha)
  hexInputText.value = hex
  emitColor(hex, isEnd)
}

function handleHueMouseDown(e: MouseEvent) {
  if (props.disabled) return
  isDraggingHue = true
  updateHueFromEvent(e, false)

  function onMouseMove(event: MouseEvent) {
    if (isDraggingHue) {
      updateHueFromEvent(event, false)
    }
  }
  function onMouseUp(event: MouseEvent) {
    if (isDraggingHue) {
      isDraggingHue = false
      updateHueFromEvent(event, true)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

// --- Alpha Slider Dragging ---
const alphaTrackRef = ref<HTMLElement | null>(null)
let isDraggingAlpha = false

function updateAlphaFromEvent(e: MouseEvent, isEnd = false) {
  if (!alphaTrackRef.value) return
  const rect = alphaTrackRef.value.getBoundingClientRect()
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
  alpha.value = Math.round((x / rect.width) * 100) / 100
  const [r, g, b] = hsvToRgb(hue.value, saturation.value, value.value)
  const hex = rgbToHex(r, g, b, alpha.value, !props.disabledAlpha)
  hexInputText.value = hex
  emitColor(hex, isEnd)
}

function handleAlphaMouseDown(e: MouseEvent) {
  if (props.disabled || props.disabledAlpha) return
  isDraggingAlpha = true
  updateAlphaFromEvent(e, false)

  function onMouseMove(event: MouseEvent) {
    if (isDraggingAlpha) {
      updateAlphaFromEvent(event, false)
    }
  }
  function onMouseUp(event: MouseEvent) {
    if (isDraggingAlpha) {
      isDraggingAlpha = false
      updateAlphaFromEvent(event, true)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

// --- Text Input Handlers ---
function handleTextInputBlur() {
  const parsed = parseColorString(hexInputText.value)
  if (parsed) {
    const [h, s, v] = rgbToHsv(parsed.r, parsed.g, parsed.b)
    hue.value = h
    saturation.value = s
    value.value = v
    alpha.value = parsed.a
    const hex = rgbToHex(parsed.r, parsed.g, parsed.b, parsed.a, !props.disabledAlpha)
    hexInputText.value = hex
    emitColor(hex, true)
  } else if (!hexInputText.value.trim() && props.allowClear) {
    handleClear()
  } else {
    // Restore current hex
    hexInputText.value = currentHex.value
  }
}

function handleTextInputEnter(e: KeyboardEvent) {
  ;(e.target as HTMLInputElement).blur()
}

function selectPresetColor(color: string) {
  if (props.disabled) return
  syncFromInput(color)
  emitColor(color, true)
}

function handleClear(e?: MouseEvent) {
  if (e) e.stopPropagation()
  if (props.disabled) return
  hexInputText.value = ''
  emit('update:modelValue', '')
  emit('update:value', '')
  emit('change', '')
  emit('changeComplete', '')
  emit('clear')
}

// --- Trigger Label ---
const triggerText = computed(() => {
  if (typeof props.showText === 'function') {
    return props.showText(currentHex.value)
  }
  return currentHex.value || props.placeholder
})
</script>

<template>
  <div
    ref="containerRef"
    class="rr-color-picker"
    :class="[
      `rr-color-picker--${size}`,
      {
        'is-disabled': disabled,
        'is-open': isOpen,
        'is-block': block
      }
    ]"
    :title="title"
  >
    <!-- Ant Design Style Trigger -->
    <div
      class="rr-color-picker__trigger"
      :tabindex="disabled ? -1 : 0"
      @click="toggleOpen"
      @keydown.enter.prevent="toggleOpen"
      @keydown.space.prevent="toggleOpen"
    >
      <span class="rr-color-picker__color-block">
        <span
          class="rr-color-picker__color-block-inner"
          :style="{ backgroundColor: currentHex || 'transparent' }"
        />
      </span>

      <span v-if="showText" class="rr-color-picker__trigger-text">
        {{ triggerText }}
      </span>

      <span
        v-if="allowClear && currentHex && !disabled"
        class="rr-color-picker__clear-btn"
        title="清除颜色"
        @click="handleClear"
      >
        <X :size="12" />
      </span>
    </div>

    <!-- Ant Design Style Popover Panel -->
    <Teleport to="body">
    <transition name="rr-slide-up">
      <div
        v-if="isOpen"
        ref="panelRef"
        class="rr-color-picker__panel"
        :style="panelStyle"
        @pointerdown.stop
      >
        <!-- 2D Saturation / Value Palette -->
        <div
          ref="paletteRef"
          class="rr-color-picker__palette"
          :style="{
            backgroundColor: `rgb(${hueColorRgb[0]}, ${hueColorRgb[1]}, ${hueColorRgb[2]})`
          }"
          @mousedown.prevent="handlePaletteMouseDown"
        >
          <div class="rr-color-picker__palette-white" />
          <div class="rr-color-picker__palette-black" />
          <div
            class="rr-color-picker__palette-handle"
            :style="{
              left: `${saturation * 100}%`,
              top: `${(1 - value) * 100}%`,
              backgroundColor: currentHex || '#FFFFFF'
            }"
          />
        </div>

        <!-- Sliders Area -->
        <div class="rr-color-picker__sliders">
          <!-- Hue Slider -->
          <div
            ref="hueTrackRef"
            class="rr-color-picker__slider rr-color-picker__slider--hue"
            @mousedown.prevent="handleHueMouseDown"
          >
            <div
              class="rr-color-picker__slider-handle"
              :style="{ left: `${(hue / 360) * 100}%` }"
            />
          </div>

          <!-- Alpha Slider -->
          <div
            v-if="!disabledAlpha"
            ref="alphaTrackRef"
            class="rr-color-picker__slider rr-color-picker__slider--alpha"
            @mousedown.prevent="handleAlphaMouseDown"
          >
            <div
              class="rr-color-picker__slider-alpha-bg"
              :style="{
                background: `linear-gradient(to right, transparent, rgb(${currentRgb[0]}, ${currentRgb[1]}, ${currentRgb[2]}))`
              }"
            />
            <div
              class="rr-color-picker__slider-handle"
              :style="{ left: `${alpha * 100}%` }"
            />
          </div>
        </div>

        <!-- Input & Format Section -->
        <div class="rr-color-picker__input-row">
          <span class="rr-color-picker__format-tag">HEX</span>
          <input
            v-model="hexInputText"
            type="text"
            class="rr-color-picker__hex-input"
            maxlength="9"
            placeholder="#000000"
            @blur="handleTextInputBlur"
            @keydown.enter="handleTextInputEnter"
          />
          <button
            v-if="allowClear"
            type="button"
            class="rr-color-picker__clear-text-btn"
            @click="handleClear"
          >
            清除
          </button>
        </div>

        <!-- Presets -->
        <div v-if="normalizedPresets && normalizedPresets.length" class="rr-color-picker__presets">
          <div v-for="(group, idx) in normalizedPresets" :key="idx" class="rr-color-picker__preset-group">
            <div v-if="group.label" class="rr-color-picker__preset-title">
              {{ group.label }}
            </div>
            <div class="rr-color-picker__preset-list">
              <button
                v-for="color in group.colors"
                :key="color"
                type="button"
                class="rr-color-picker__preset-swatch"
                :class="{
                  'is-active': currentHex.toUpperCase() === color.toUpperCase()
                }"
                :style="{ backgroundColor: color }"
                :title="color"
                @click="selectPresetColor(color)"
              >
                <Check
                  v-if="currentHex.toUpperCase() === color.toUpperCase()"
                  :size="10"
                  class="rr-color-picker__preset-check"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    </Teleport>
  </div>
</template>

<style scoped>
.rr-color-picker {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  box-sizing: border-box;
}

.rr-color-picker.is-block {
  display: flex;
  width: 100%;
}

/* Ant Design Trigger */
.rr-color-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  background: var(--rr-color-surface, #ffffff);
  border: 1px solid var(--rr-color-border, #CBD2DA);
  border-radius: var(--rr-radius-control, 6px);
  padding: 3px 8px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease-in-out;
}

.rr-color-picker--small .rr-color-picker__trigger {
  height: 24px;
  padding: 2px 6px;
  font-size: var(--rr-font-size-xs, 12px);
}

.rr-color-picker--middle .rr-color-picker__trigger {
  height: 32px;
  padding: 3px 8px;
  font-size: var(--rr-font-size-body, 14px);
}

.rr-color-picker--large .rr-color-picker__trigger {
  height: 40px;
  padding: 4px 12px;
  font-size: var(--rr-font-size-base, 16px);
}

.rr-color-picker__trigger:hover:not(.is-disabled) {
  border-color: var(--rr-color-primary, #5A46E0);
}

.rr-color-picker.is-open .rr-color-picker__trigger,
.rr-color-picker__trigger:focus {
  border-color: var(--rr-color-primary, #5A46E0);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft, rgba(90, 70, 224, 0.12));
  outline: none;
}

.rr-color-picker.is-disabled .rr-color-picker__trigger {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--rr-color-surface-subtle, #F7F8FA);
}

/* Color block inside trigger */
.rr-color-picker__color-block {
  position: relative;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  overflow: hidden;
  background-image: linear-gradient(45deg, rgba(0, 0, 0, 0.08) 25%, transparent 25%),
                    linear-gradient(-45deg, rgba(0, 0, 0, 0.08) 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.08) 75%),
                    linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.08) 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0;
  display: inline-block;
  flex-shrink: 0;
}

.rr-color-picker--small .rr-color-picker__color-block {
  width: 16px;
  height: 16px;
  min-width: 16px;
}

.rr-color-picker--large .rr-color-picker__color-block {
  width: 28px;
  height: 28px;
  min-width: 28px;
}

.rr-color-picker__color-block-inner {
  display: block;
  width: 100%;
  height: 100%;
}

.rr-color-picker__trigger-text {
  flex: 1;
  min-width: 0;
  font-family: var(--rr-font-code, monospace);
  font-size: 13px;
  color: var(--rr-color-text, #2B3038);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rr-color-picker__clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--rr-color-text-subtle, #8A94A2);
  margin-left: 2px;
  border-radius: 50%;
  transition: color 0.15s ease;
}

.rr-color-picker__clear-btn:hover {
  color: var(--rr-color-text, #2B3038);
}

/* Ant Design Popover Panel */
.rr-color-picker__panel {
  position: fixed;
  z-index: var(--rr-z-dropdown, 1050);
  width: 248px;
  background: var(--rr-color-surface, #ffffff);
  border: 1px solid var(--rr-color-border, #CBD2DA);
  border-radius: var(--rr-radius-panel, 8px);
  box-shadow: var(--rr-shadow-2, 0 4px 12px rgba(15, 23, 42, 0.1));
  padding: 12px;
  box-sizing: border-box;
}

/* 2D Saturation Palette */
.rr-color-picker__palette {
  position: relative;
  width: 100%;
  height: 140px;
  border-radius: 4px;
  overflow: hidden;
  cursor: crosshair;
  user-select: none;
}

.rr-color-picker__palette-white {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, #ffffff, transparent);
}

.rr-color-picker__palette-black {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, #000000, transparent);
}

.rr-color-picker__palette-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-sizing: border-box;
}

/* Sliders */
.rr-color-picker__sliders {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.rr-color-picker__slider {
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
}

.rr-color-picker__slider--hue {
  background: linear-gradient(
    to right,
    rgb(255, 0, 0) 0%,
    rgb(255, 255, 0) 17%,
    rgb(0, 255, 0) 33%,
    rgb(0, 255, 255) 50%,
    rgb(0, 0, 255) 67%,
    rgb(255, 0, 255) 83%,
    rgb(255, 0, 0) 100%
  );
}

.rr-color-picker__slider--alpha {
  background-image: linear-gradient(45deg, rgba(0, 0, 0, 0.08) 25%, transparent 25%),
                    linear-gradient(-45deg, rgba(0, 0, 0, 0.08) 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.08) 75%),
                    linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.08) 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0;
  overflow: hidden;
}

.rr-color-picker__slider-alpha-bg {
  position: absolute;
  inset: 0;
}

.rr-color-picker__slider-handle {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: #ffffff;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-sizing: border-box;
}

/* Input Row */
.rr-color-picker__input-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}

.rr-color-picker__format-tag {
  font-size: 11px;
  font-weight: 600;
  color: var(--rr-color-text-subtle, #8A94A2);
  padding: 2px 4px;
  background: var(--rr-color-surface-subtle, #F7F8FA);
  border-radius: 4px;
  border: 1px solid var(--rr-color-border, #CBD2DA);
}

.rr-color-picker__hex-input {
  flex: 1;
  min-width: 0;
  height: 26px;
  padding: 0 6px;
  border: 1px solid var(--rr-color-border, #CBD2DA);
  border-radius: 4px;
  font-size: 12px;
  font-family: var(--rr-font-code, monospace);
  color: var(--rr-color-text, #2B3038);
  background: var(--rr-color-surface, #ffffff);
  box-sizing: border-box;
  outline: none;
}

.rr-color-picker__hex-input:focus {
  border-color: var(--rr-color-primary, #5A46E0);
}

.rr-color-picker__clear-text-btn {
  height: 26px;
  padding: 0 8px;
  font-size: 12px;
  color: var(--rr-color-text-muted, #67717F);
  background: transparent;
  border: 1px solid var(--rr-color-border, #CBD2DA);
  border-radius: 4px;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.rr-color-picker__clear-text-btn:hover {
  color: var(--rr-color-danger, #BE3038);
  border-color: var(--rr-color-danger, #BE3038);
}

/* Presets */
.rr-color-picker__presets {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--rr-color-border, #CBD2DA);
}

.rr-color-picker__preset-title {
  font-size: 12px;
  color: var(--rr-color-text-muted, #67717F);
  margin-bottom: 6px;
}

.rr-color-picker__preset-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.rr-color-picker__preset-swatch {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease;
}

.rr-color-picker__preset-swatch:hover {
  transform: scale(1.15);
}

.rr-color-picker__preset-swatch.is-active {
  box-shadow: 0 0 0 2px var(--rr-color-primary, #5A46E0);
}

.rr-color-picker__preset-check {
  color: #ffffff;
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.8));
}
</style>
